import "./application";
import '../apps/login/main';
import '../apps/shareable_copy_modal/main';
import '../apps/on_boarding/main.ts';
import '../utils/helpers/datetimeLocalization';
import '../utils/contentCommentsSeen';
import '../apps/planned_trails/main.ts';
import '../apps/import_recording/main.ts';
import '../apps/recorded_trails/main.ts';
import '../apps/recording_crop_app/main.ts';
import '../apps/flyover_preview/main.ts';
import '../apps/recording_map/main.ts';
import '../apps/plan_map/main.ts';
import '../apps/purchases/main';
import '../apps/hikes/main';
import '../apps/hike_map/main.ts';
import '../apps/download_modal/main.ts';
import '../apps/mobile_login_qr/main.ts';
import '../apps/completed_hikes/main.ts';
import '../apps/api_token_modal/main.ts';
import '../utils/share_link_modal';
import { auth, sendPasswordResetEmail, signInWithEmailAndPassword } from "../utils/firebase/auth";
import { initAuthSync } from "../utils/firebase/authSync";
import performLogout from '../utils/auth/logout';

// Firebase is already initialized via the static import above.
// Call initAuthSync synchronously so onAuthStateChanged starts listening
// immediately — no dynamic-import delay.
if ((window as any).firebaseConfigs) {
  initAuthSync();
}

const userLogoutButton = document.getElementById('user-logout-button')

if (userLogoutButton) {
  userLogoutButton.addEventListener('click', (e) => {
    e.preventDefault();
    performLogout()
      .then(() => {
        window.location.href = '/login';
      })
      .catch((error) => {
        console.error('Error during logout:', error);
        window.location.href = '/logout';
      });
  });
}

const emailInput = document.getElementById('email');
const resetButton = document.getElementById('reset-button');
const successMessage = document.getElementById('success-message');
const errorMessage = document.getElementById('error-message');
const errorText = document.getElementById('error-text');
const hiikerApiAccountSetupContainer = document.getElementById('hiiker-api-account-setup');
const hiikerApiAccountSetupPasswordInput = document.getElementById('account-setup-password') as HTMLInputElement | null;
const hiikerApiAccountSetupPasswordConfirmationInput = document.getElementById('account-setup-password-confirmation') as HTMLInputElement | null;
const hiikerApiAccountSetupButton = document.getElementById('account-setup-button') as HTMLButtonElement | null;
const hiikerApiAccountSetupSuccessMessage = document.getElementById('account-setup-success-message');
const hiikerApiAccountSetupErrorMessage = document.getElementById('account-setup-error-message');
const hiikerApiAccountSetupErrorText = document.getElementById('account-setup-error-text');

if (emailInput && resetButton && successMessage && errorMessage && errorText) {
  resetButton.addEventListener('click', async function() {
    const email = emailInput.value.trim();

    if (!email) {
      showError('Please enter your email address');
      return;
    }

    if (!isValidEmail(email)) {
      showError('Please enter a valid email address');
      return;
    }

    resetButton.disabled = true;
    resetButton.textContent = 'Sending...';

    try {

      await sendPasswordResetEmail(auth, email);

      // Hide any error messages
      errorMessage.style.display = 'none';

      // Show success message
      successMessage.style.display = 'block';

      // Clear the email input
      emailInput.value = '';

      // Reset button state
      resetButton.disabled = false;
      resetButton.textContent = 'Send Reset Email';

    } catch (error) {
      console.error('Password reset error:', error);

      let errorMsg = 'An error occurred. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMsg = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = 'Invalid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = 'Too many requests. Please try again later.';
      }

      showError(errorMsg);

      // Reset button state
      resetButton.disabled = false;
      resetButton.textContent = 'Send Reset Email';
    }
  });

// Allow Enter key to submit
  emailInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
      resetButton.click();
    }
  });

  function isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  function showError(message) {
    errorText.textContent = message;
    errorMessage.style.display = 'block';
    successMessage.style.display = 'none';
  }
}

if (
  hiikerApiAccountSetupContainer &&
  hiikerApiAccountSetupPasswordInput &&
  hiikerApiAccountSetupPasswordConfirmationInput &&
  hiikerApiAccountSetupButton &&
  hiikerApiAccountSetupSuccessMessage &&
  hiikerApiAccountSetupErrorMessage &&
  hiikerApiAccountSetupErrorText
) {
  const email = hiikerApiAccountSetupContainer.getAttribute('data-email') || '';
  const token = hiikerApiAccountSetupContainer.getAttribute('data-token') || '';

  hiikerApiAccountSetupButton.addEventListener('click', async function() {
    const password = hiikerApiAccountSetupPasswordInput.value;
    const passwordConfirmation = hiikerApiAccountSetupPasswordConfirmationInput.value;

    if (!token || !email) {
      showHiikerApiAccountSetupError('This account setup link is invalid or incomplete.');
      return;
    }

    if (password.length < 6) {
      showHiikerApiAccountSetupError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== passwordConfirmation) {
      showHiikerApiAccountSetupError('Password confirmation does not match.');
      return;
    }

    hiikerApiAccountSetupButton.disabled = true;
    hiikerApiAccountSetupButton.textContent = 'Setting up...';
    hiikerApiAccountSetupErrorMessage.style.display = 'none';
    hiikerApiAccountSetupSuccessMessage.style.display = 'none';

    try {
      const response = await fetch('/user/hiiker-api/account-setup', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password,
          password_confirmation: passwordConfirmation
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'We could not finish setting up this account.');
      }

      hiikerApiAccountSetupSuccessMessage.textContent = data.message || 'Password set successfully. Signing you in...';
      hiikerApiAccountSetupSuccessMessage.style.display = 'block';
      try {
        await signInAfterHiikerApiAccountSetup(email, password);
      } catch (error: any) {
        console.error('Automatic sign-in after HiiKER API account setup failed:', error);
        showHiikerApiAccountSetupError('Your password was set, but we could not sign you in automatically. Redirecting to login...');
        window.setTimeout(() => {
          window.location.assign(data.login_url || '/login');
        }, 1500);
      }
    } catch (error: any) {
      console.error('HiiKER API account setup error:', error);
      showHiikerApiAccountSetupError(error.message || 'An error occurred. Please try again.');
      hiikerApiAccountSetupButton.disabled = false;
      hiikerApiAccountSetupButton.textContent = 'Set Password & Sign In';
    }
  });

  hiikerApiAccountSetupPasswordConfirmationInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
      hiikerApiAccountSetupButton.click();
    }
  });

  function showHiikerApiAccountSetupError(message) {
    hiikerApiAccountSetupErrorText.textContent = message;
    hiikerApiAccountSetupErrorMessage.style.display = 'block';
    hiikerApiAccountSetupSuccessMessage.style.display = 'none';
  }
}

async function signInAfterHiikerApiAccountSetup(email: string, password: string) {
  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken(true);
      window.location.assign(`/firebase/auth?accessToken=${encodeURIComponent(idToken)}`);
      return;
    } catch (error) {
      lastError = error;
      await waitForHiikerApiAccountSetupSync();
    }
  }

  throw lastError;
}

function waitForHiikerApiAccountSetupSync() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 600);
  });
}
